import { useState, useEffect } from 'react';
import { Modal } from './ui';
import ImageUploader from './ImageUploader';
import FileUploader from './FileUploader';
import AttachmentPreview from './AttachmentPreview';

const EditEventModal = ({ isOpen, onClose, event, onSave, userPermissions }) => {
  const [formData, setFormData] = useState({});
  const [editReason, setEditReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showEditHistory, setShowEditHistory] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  
  // File upload states
  const [currentAttachments, setCurrentAttachments] = useState(null);
  const [newBannerImage, setNewBannerImage] = useState(null);
  const [newGalleryImages, setNewGalleryImages] = useState([]);
  const [newAttachments, setNewAttachments] = useState([]);
  const [removedAttachments, setRemovedAttachments] = useState([]);

  // Get backend URL from environment
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (event && isOpen) {
      // Initialize form data with current event data
      setFormData({
        title: event.title || '',
        description: event.description || '',
        event_type: event.event_type || '',
        start_date: event.start_date ? new Date(event.start_date).toISOString().slice(0, 16) : '',
        end_date: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
        location: event.location || '',
        venue: event.venue || '',
        max_attendees: event.max_attendees || '',
        price: event.price || 0,
        tags: event.tags || [],
        links: event.links || [],
        registration_deadline: event.registration_deadline ? new Date(event.registration_deadline).toISOString().slice(0, 16) : ''
      });
      
      // Reset other states
      setEditReason('');
      setNewBannerImage(null);
      setNewGalleryImages([]);
      setNewAttachments([]);
      setRemovedAttachments([]);
      
      // Fetch current attachments
      fetchCurrentAttachments();
    }
  }, [event, isOpen]);

  const fetchCurrentAttachments = async () => {
    if (!event?.id) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/events/${event.id}/attachments`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const attachments = await response.json();
        setCurrentAttachments(attachments);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };

  const fetchEditHistory = async () => {
    if (!event?.id) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/events/${event.id}/edit-history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const history = await response.json();
        setEditHistory(history.edit_history || []);
        setShowEditHistory(true);
      }
    } catch (error) {
      console.error('Error fetching edit history:', error);
    }
  };

  const isFieldRestricted = (fieldName) => {
    if (!userPermissions?.restrictions) return false;
    return userPermissions.restrictions.some(restriction => 
      restriction.includes(fieldName) || restriction.includes('major_changes')
    );
  };

  const hasRegistrations = () => {
    return userPermissions?.event_status?.registrations_count > 0;
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    return new Date(dateTimeString).toISOString().slice(0, 16);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTagChange = (tags) => {
    setFormData(prev => ({
      ...prev,
      tags: tags
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show confirmation for critical changes
    if (hasRegistrations() && (formData.start_date !== formatDateTime(event.start_date) || formData.location !== event.location)) {
      setShowConfirmDialog(true);
      return;
    }
    
    await performUpdate();
  };

  const performUpdate = async () => {
    setLoading(true);
    
    try {
      // Prepare update data - only include changed fields
      const updateData = {};
      
      // Check each field for changes
      Object.keys(formData).forEach(key => {
        const currentValue = formData[key];
        let originalValue = event[key];
        
        // Handle datetime fields
        if (key.includes('date') && originalValue) {
          originalValue = formatDateTime(originalValue);
        }
        
        // Handle arrays (tags)
        if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
          if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
            updateData[key] = currentValue;
          }
        }
        // Handle other fields
        else if (currentValue !== originalValue && currentValue !== '') {
          if (key.includes('date') && currentValue) {
            updateData[key] = new Date(currentValue).toISOString();
          } else {
            updateData[key] = currentValue;
          }
        }
      });

      // Only proceed if there are actual changes
      if (Object.keys(updateData).length === 0 && !newBannerImage && newGalleryImages.length === 0 && newAttachments.length === 0 && removedAttachments.length === 0) {
        alert('No changes to save');
        setLoading(false);
        return;
      }

      console.log('🔄 Updating event with data:', updateData);

      // Update event data
      const response = await fetch(`${backendUrl}/api/events/${event.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...updateData,
          edit_reason: editReason || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Event updated successfully:', result);

      // Handle file uploads if any
      await handleFileUploads();

      // Call onSave callback
      if (onSave) {
        await onSave(result.event);
      }

      // Close modal
      onClose();
      alert(`Event updated successfully! ${result.changes_made} changes made.`);
      
    } catch (error) {
      console.error('❌ Error updating event:', error);
      alert(`Error updating event: ${error.message}`);
    } finally {
      setLoading(false);
      setShowConfirmDialog(false);
    }
  };

  const handleFileUploads = async () => {
    try {
      // Upload new banner image
      if (newBannerImage) {
        const formData = new FormData();
        formData.append('file', newBannerImage);
        formData.append('event_id', event.id);
        formData.append('is_banner', 'true');
        
        await fetch(`${backendUrl}/api/events/upload/image`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: formData
        });
      }

      // Upload new gallery images
      for (const image of newGalleryImages) {
        const formData = new FormData();
        formData.append('file', image);
        formData.append('event_id', event.id);
        formData.append('is_banner', 'false');
        
        await fetch(`${backendUrl}/api/events/upload/image`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: formData
        });
      }

      // Upload new attachments
      for (const attachment of newAttachments) {
        const formData = new FormData();
        formData.append('file', attachment);
        formData.append('event_id', event.id);
        
        await fetch(`${backendUrl}/api/events/upload/attachment`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: formData
        });
      }

      // Delete removed attachments
      for (const attachmentId of removedAttachments) {
        await fetch(`${backendUrl}/api/events/attachments/${attachmentId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }
    } catch (error) {
      console.error('Error handling file uploads:', error);
    }
  };

  if (!isOpen || !event) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} maxWidth="5xl">
        <div className="p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <span className="text-2xl mr-3">✏️</span>
              Edit Event: {event.title}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {/* Permissions Warning */}
          {hasRegistrations() && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-3">⚠️</span>
                <div>
                  <p className="font-semibold text-yellow-800">
                    {userPermissions.event_status.registrations_count} people registered for this event
                  </p>
                  <p className="text-sm text-yellow-700">
                    Some fields may be restricted to prevent disruption to registered attendees.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Event Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isFieldRestricted('title')}
                required
              />
            </div>

            {/* Event Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                required
              />
            </div>

            {/* Date and Time */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date & Time * {isFieldRestricted('date') && '🔒'}
                </label>
                <input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isFieldRestricted('date')}
                  required
                />
                {isFieldRestricted('date') && (
                  <p className="text-xs text-gray-500 mt-1">Cannot change date due to existing registrations</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isFieldRestricted('date')}
                  required
                />
              </div>
            </div>

            {/* Location */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location * {isFieldRestricted('location') && '🔒'}
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isFieldRestricted('location')}
                  required
                />
                {isFieldRestricted('location') && (
                  <p className="text-xs text-gray-500 mt-1">Cannot change location due to existing registrations</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Venue
                </label>
                <input
                  type="text"
                  value={formData.venue}
                  onChange={(e) => handleInputChange('venue', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Capacity and Price */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Attendees
                </label>
                <input
                  type="number"
                  value={formData.max_attendees}
                  onChange={(e) => handleInputChange('max_attendees', parseInt(e.target.value) || '')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min={userPermissions?.event_status?.registrations_count || 0}
                />
                {hasRegistrations() && (
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum: {userPermissions.event_status.registrations_count} (current registrations)
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price (IDR)
                </label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
            </div>

            {/* Current Attachments */}
            {currentAttachments && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📎 Current Media & Files</h3>
                <AttachmentPreview 
                  attachments={currentAttachments} 
                  showDelete={true}
                  onDelete={(attachmentId) => {
                    setRemovedAttachments(prev => [...prev, attachmentId]);
                  }}
                />
              </div>
            )}

            {/* Event Links */}
            <div className="space-y-4 border-t border-gray-200 pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                🔗 Event Links (Optional)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Add related links like registration pages, websites, or social media.
              </p>
              <div className="space-y-3">
                {(formData.links || []).map((link, index) => (
                  <div key={index} className="flex space-x-2 items-start">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Link title (e.g., 'Register Here', 'Event Website')"
                        value={link.title}
                        onChange={(e) => {
                          const updatedLinks = [...(formData.links || [])];
                          updatedLinks[index].title = e.target.value;
                          handleInputChange('links', updatedLinks);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="url"
                        placeholder="https://example.com"
                        value={link.url}
                        onChange={(e) => {
                          const updatedLinks = [...(formData.links || [])];
                          updatedLinks[index].url = e.target.value;
                          handleInputChange('links', updatedLinks);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updatedLinks = (formData.links || []).filter((_, i) => i !== index);
                        handleInputChange('links', updatedLinks);
                      }}
                      className="px-3 py-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const currentLinks = formData.links || [];
                    handleInputChange('links', [...currentLinks, { title: '', url: '', description: '' }]);
                  }}
                  className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-300 hover:text-blue-500 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>➕</span>
                  <span>Add Link</span>
                </button>
              </div>
            </div>

            {/* New File Uploads */}
            <div className="space-y-6 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900">📎 Add New Media & Files</h3>
              
              {/* New Banner Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  🖼️ Replace Banner Image (Optional)
                </label>
                <ImageUploader
                  onUpload={(files) => setNewBannerImage(files[0])}
                  multiple={false}
                  maxFiles={1}
                />
              </div>

              {/* New Gallery Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  📸 Add Gallery Images (Optional)
                </label>
                <ImageUploader
                  onUpload={(files) => setNewGalleryImages(prev => [...prev, ...files])}
                  multiple={true}
                  maxFiles={5}
                />
              </div>

              {/* New Attachments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  📎 Add New Attachments (Optional)
                </label>
                <FileUploader
                  onUpload={(files) => setNewAttachments(prev => [...prev, ...files])}
                  maxFiles={10}
                />
              </div>
            </div>

            {/* Edit Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📝 Reason for Edit {userPermissions?.level === 'full' && '(Required for admin changes)'}
              </label>
              <textarea
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Briefly describe why you're making these changes..."
                required={userPermissions?.level === 'full'}
              />
            </div>

            {/* Change Impact */}
            {hasRegistrations() && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">📊 Change Impact:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Will notify {userPermissions.event_status.registrations_count} registered attendees</li>
                  <li>• Changes will be logged in event history</li>
                  <li>• Attendees can cancel if they disagree with changes</li>
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={fetchEditHistory}
                className="px-4 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                📊 View Edit History
              </button>
              
              <div className="space-x-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>💾 Save Changes</span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </Modal>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <Modal isOpen={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="lg">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-4">⚠️</span>
              <h3 className="text-xl font-semibold text-gray-900">Confirm Critical Changes</h3>
            </div>
            <p className="text-gray-600 mb-6">
              You're about to make changes that will affect {userPermissions?.event_status?.registrations_count} registered attendees. 
              They will be notified of these changes. Are you sure you want to proceed?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={performUpdate}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Yes, Make Changes
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit History Modal */}
      {showEditHistory && (
        <Modal isOpen={showEditHistory} onClose={() => setShowEditHistory(false)} maxWidth="4xl">
          <div className="p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">📊 Edit History</h3>
            {editHistory.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No edit history available</p>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {editHistory.map((entry, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-medium text-gray-900">{entry.editor_name}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(entry.edited_at).toLocaleString()}
                        </p>
                      </div>
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {Object.keys(entry.changes).length} changes
                      </span>
                    </div>
                    
                    {entry.edit_reason && (
                      <p className="text-sm text-gray-600 mb-3 italic">
                        "{entry.edit_reason}"
                      </p>
                    )}
                    
                    <div className="space-y-2">
                      {Object.entries(entry.changes).map(([field, newValue]) => (
                        <div key={field} className="text-sm">
                          <span className="font-medium text-gray-700 capitalize">
                            {field.replace('_', ' ')}:
                          </span>
                          <div className="ml-4 flex items-center space-x-2">
                            <span className="text-red-600 line-through">
                              {entry.old_values[field] || 'empty'}
                            </span>
                            <span>→</span>
                            <span className="text-green-600">
                              {newValue || 'empty'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default EditEventModal;